import { Button } from "../atoms/Button.js";
import { Icon } from "../icons/Icon.js";
import { useT } from "../i18n/useT.js";

interface ErrorStateProps {
  title: string;
  message?: string;
  /** Omit to render without a Retry button (rare — most call sites have one). */
  onRetry?: () => void;
  retryLabel?: string;
  variant?: "card" | "list" | "panel";
  className?: string;
}

/**
 * Peer of `EmptyState` for the "query gave up retrying" case (see
 * `useQuery`'s `retry()`) — same structure/variants, plus a warning icon and
 * a Retry action. Never render this while a query is still `waiting`
 * (mid-retry): the `QueryStatus` pill already covers that in-flight state.
 */
export function ErrorState({ title, message, onRetry, retryLabel, variant = "card", className = "" }: ErrorStateProps) {
  const t = useT();
  const rootClass =
    variant === "card"
      ? `mmm-card mmm-empty-state mmm-error-state ${className}`.trim()
      : `mmm-empty-state mmm-empty-state--${variant} mmm-error-state ${className}`.trim();

  const action = onRetry ? (
    <Button variant="secondary" onClick={onRetry}>
      {retryLabel ?? t("common.retry")}
    </Button>
  ) : undefined;

  if (variant === "card") {
    return (
      <div className={rootClass} role="alert">
        <Icon name="alert" className="mmm-error-state-icon" />
        <h2>{title}</h2>
        {message && <p>{message}</p>}
        {action}
      </div>
    );
  }

  return (
    <div className={rootClass} role="alert">
      <Icon name="alert" className="mmm-error-state-icon" />
      <strong>{title}</strong>
      {message && <span>{message}</span>}
      {action}
    </div>
  );
}
